import { useEffect } from 'react';

/**
 * Блокировка прокрутки под открытым слоем.
 *
 * Правило для всего приложения: пока поверх экрана висит окно, лист меню или
 * шторка уведомлений, страница под ними не двигается. Иначе палец, ведущий
 * по окну, листает ленту за ним — закрыв окно, человек оказывается не там,
 * где был.
 *
 * Две вещи, из-за которых простого `overflow: hidden` не хватает:
 *
 * 1. Слои открываются друг поверх друга. Каждый при закрытии возвращал бы
 *    прежнее значение, но «прежнее» он запомнил уже заблокированным —
 *    и прокрутка не восстанавливалась. Отсюда счётчик: снимаем блокировку
 *    только когда закрылся последний слой.
 *
 * 2. В webview на iOS `overflow: hidden` у body не держит прокрутку —
 *    страница всё равно тянется. Работает только `position: fixed`, но он
 *    сбрасывает позицию наверх, поэтому её приходится запоминать и
 *    возвращать руками.
 */

let depth = 0;
let savedY = 0;

/**
 * Захватить блокировку.
 *
 * Экспортируется отдельно от хука, чтобы счётчик слоёв можно было проверить
 * тестом: подменить `useEffect` внутри модуля React нельзя, а вызвать пару
 * функций — можно.
 */
export function lockScroll() {
  depth += 1;
  if (depth > 1) return;

  savedY = window.scrollY;

  const body = document.body;
  body.style.position = 'fixed';
  body.style.top = `-${savedY}px`;
  body.style.left = '0';
  body.style.right = '0';
  body.style.width = '100%';
  body.style.overflow = 'hidden';
}

export function unlockScroll() {
  depth = Math.max(0, depth - 1);
  if (depth > 0) return;

  const body = document.body;
  body.style.position = '';
  body.style.top = '';
  body.style.left = '';
  body.style.right = '';
  body.style.width = '';
  body.style.overflow = '';

  // Возвращаем туда, где человек был. `instant`, а не плавно: плавная
  // прокрутка после закрытия окна выглядит как самопроизвольное движение.
  window.scrollTo({ top: savedY, behavior: 'instant' as ScrollBehavior });
}

/** Держит прокрутку заблокированной, пока `active` истинно. */
export function useScrollLock(active: boolean) {
  useEffect(() => {
    if (!active) return;

    lockScroll();
    return unlockScroll;
  }, [active]);
}
